import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere token
api.interceptors.request.use(
  (config) => {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const { state } = JSON.parse(authData);
      if (state?.token) {
        config.headers.Authorization = `Bearer ${state.token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor per gestire errori
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isGlsTest = url.includes('/shipping/gls/');
    if (error.response?.status === 401 && !isGlsTest) {
      localStorage.removeItem('auth-storage');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  deleteAccount: () => api.delete('/auth/me'),
};

// Products APIs
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }).then(res => res.data.data),
  getById: (id) => api.get(`/products/${id}`).then(res => res.data.data),
  getYears: () => api.get('/products/years').then(res => res.data.data),
  create: (data) => api.post('/products', data).then(res => res.data.data),
  update: (id, data) => api.put(`/products/${id}`, data).then(res => res.data.data),
  delete: (id) => api.delete(`/products/${id}`).then(res => res.data),
  addReview: (id, data) => api.post(`/products/${id}/reviews`, data).then(res => res.data.data),
  deleteReview: (id, reviewId) => api.delete(`/products/${id}/reviews/${reviewId}`).then(res => res.data.data),
  uploadImage: (id, formData) => {
    return api.post(`/products/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  deleteImage: (id, imageIndex) => api.delete(`/products/${id}/images/${imageIndex}`).then(res => res.data),
};

// Producers APIs
export const producersAPI = {
  getAll: () => api.get('/producers').then(res => res.data.data),
  getAllAdmin: () => api.get('/producers/admin').then(res => res.data.data),
  getById: (id) => api.get(`/producers/${id}`).then(res => res.data.data),
  create: (data) => api.post('/producers', data).then(res => res.data.data),
  update: (id, data) => api.put(`/producers/${id}`, data).then(res => res.data.data),
  delete: (id) => api.delete(`/producers/${id}`).then(res => res.data),
  uploadPdf: (id, formData) => {
    return api.post(`/producers/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  }
};

// Orders APIs
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getMyOrders: () => api.get('/orders/my-orders'),
  getAll: (params) => api.get('/orders', { params }).then(res => res.data.data),
  getById: (id) => api.get(`/orders/${id}`).then(res => res.data.data),
  getByCustomer: (customerId) => api.get(`/orders/customer/${customerId}`).then(res => res.data.data),
  createPaymentIntent: (orderId) => api.post(`/orders/${orderId}/payment-intent`),
  createPaypalOrder: (orderId) => api.post(`/orders/${orderId}/paypal/create`),
  capturePaypalOrder: (orderId, payload) => api.post(`/orders/${orderId}/paypal/capture`, payload || {}),
  pollPaypalCapture: (orderId) => api.post(`/orders/${orderId}/paypal/poll`),
  cancelOrder: (orderId) => api.post(`/orders/${orderId}/cancel`),
  confirmPayment: (orderId) => api.post(`/orders/${orderId}/confirm`),
  updateStatus: (orderId, stato) => api.put(`/orders/${orderId}/status`, { stato }).then(res => res.data.data),
  updateShippingSimulationStatus: (orderId, simulatedStatus) =>
    api.put(`/orders/${orderId}/shipping-simulation`, { simulatedStatus }).then(res => res.data.data),
};

// Shipping APIs
export const shippingAPI = {
  getConfig: () => api.get('/shipping/config').then(res => res.data.data),
  updateConfig: (data) => api.put('/shipping/config', data).then(res => res.data.data),
  getGlsStatus: () => api.get('/shipping/gls/status').then(res => res.data.data),
  createGlsShipment: (orderId, payload) => api.post(`/shipping/gls/shipments/${orderId}`, payload ? { payload } : {}).then(res => res.data.data),
  getGlsTracking: (trackingNumber) => api.get(`/shipping/gls/tracking/${trackingNumber}`).then(res => res.data.data),
  testGlsAuth: () => api.get('/shipping/gls/test-auth').then(res => res.data.data),
  testGlsShipItServices: (payload) => api.post('/shipping/gls/test-shipit-services', payload ? { payload } : {}).then(res => res.data.data),
  testGlsTrackTrace: (unitno, lang) => api.get('/shipping/gls/test-tracktrace', { params: { unitno, lang } }).then(res => res.data.data),
};

// CRM APIs
export const crmAPI = {
  getStats: () => api.get('/crm/stats').then(res => res.data.data),
  getCustomers: (params) => api.get('/crm/customers', { params }).then(res => res.data.data),
  getCustomerById: (id) => api.get(`/crm/customers/${id}`).then(res => res.data.data),
  updateCustomer: (id, data) => api.put(`/crm/customers/${id}`, data).then(res => res.data.data),
  addActivity: (customerId, data) => api.post('/crm/activities', { ...data, cliente: customerId }).then(res => res.data.data),
  getActivities: (customerId) => api.get(`/crm/customers/${customerId}/activities`).then(res => res.data.data),
  getMarketingInsights: () => api.get('/crm/marketing-insights').then(res => res.data.data),
  getAbandonedCarts: (params) => api.get('/crm/abandoned-carts', { params }).then(res => res.data),
};

// Email APIs
export const emailAPI = {
  getConfig: () => api.get('/email/config').then(res => res.data.data),
  updateConfig: (data) => api.put('/email/config', data).then(res => res.data.data),
  sendTest: (email) => api.post('/email/test', { emailTest: email }).then(res => res.data),
  sendAbandonedCartTest: (email) => api.post('/email/abandoned-cart/test', { emailTest: email }).then(res => res.data),
};

// Commercial Requests APIs
export const commercialRequestsAPI = {
  create: (data) => api.post('/commercial-requests', data).then(res => res.data),
};

// Cancel Requests APIs
export const cancelRequestsAPI = {
  create: (data) => api.post('/cancel-requests', data).then(res => res.data),
};

// Marketing APIs
export const marketingAPI = {
  getCampaigns: () => api.get('/marketing/campaigns').then(res => res.data.data),
  createCampaign: (data) => api.post('/marketing/campaigns', data).then(res => res.data.data),
  sendCampaign: (id) => api.post(`/marketing/campaigns/${id}/send`).then(res => res.data.data),
  deleteCampaign: (id) => api.delete(`/marketing/campaigns/${id}`).then(res => res.data),
  previewCampaign: (criteria) => api.post('/marketing/campaigns/preview', { criteri: criteria }).then(res => res.data.data),
  trackVisit: (data) => api.post('/marketing/track', data).then(res => res.data.data),
  getTemplates: () => api.get('/marketing/templates').then(res => res.data.data),
  createTemplate: (data) => api.post('/marketing/templates', data).then(res => res.data.data),
  updateTemplate: (id, data) => api.put(`/marketing/templates/${id}`, data).then(res => res.data.data),
  deleteTemplate: (id) => api.delete(`/marketing/templates/${id}`).then(res => res.data),
};

// Cart APIs
export const cartAPI = {
  track: (data) => api.post('/cart/track', data).then(res => res.data.data),
};

// Cellar APIs
export const cellarAPI = {
  getSummary: () => api.get('/cantina').then(res => res.data.data),
  updateItem: (productId, data) => api.patch(`/cantina/items/${productId}`, data).then(res => res.data.data),
};

// Promotions APIs
export const promotionsAPI = {
  getAll: () => api.get('/promotions').then(res => res.data.data),
  create: (data) => api.post('/promotions', data).then(res => res.data.data),
  update: (id, data) => api.put(`/promotions/${id}`, data).then(res => res.data.data),
  validate: (data) => api.post('/promotions/validate', data).then(res => res.data.data),
  eligibleCount: (criteria) => api.post('/promotions/eligible', { criteri: criteria }).then(res => res.data.data),
};

// Score Rewards APIs
export const scoreRewardsAPI = {
  getRules: () => api.get('/score-rewards/rules').then(res => res.data.data),
  createRule: (data) => api.post('/score-rewards/rules', data).then(res => res.data.data),
  updateRule: (id, data) => api.put(`/score-rewards/rules/${id}`, data).then(res => res.data.data),
  getRewards: (params) => api.get('/score-rewards/rewards', { params }).then(res => res.data.data),
};

// Direct Sales APIs
export const directSalesAPI = {
  getAll: (params) => api.get('/direct-sales', { params }).then(res => res.data.data),
  getById: (id) => api.get(`/direct-sales/${id}`).then(res => res.data.data),
  create: (data) => api.post('/direct-sales', data).then(res => res.data.data),
  delete: (id) => api.delete(`/direct-sales/${id}`).then(res => res.data),
  getStats: () => api.get('/direct-sales/stats/summary').then(res => res.data.data),
};

// Invoices APIs
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }).then(res => res.data.data),
  getById: (id) => api.get(`/invoices/${id}`).then(res => res.data.data),
  createManual: (data) => api.post('/invoices/manual', data).then(res => res.data.data),
  createFromOrder: (orderId, data) => api.post(`/invoices/order/${orderId}`, data).then(res => res.data.data),
  createFromDirectSale: (saleId) => api.post(`/invoices/direct-sale/${saleId}`).then(res => res.data.data),
  send: (id) => api.post(`/invoices/${id}/send`).then(res => res.data.data),
  download: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),
  report: (params) => api.get('/invoices/report', { params }).then(res => res.data),
  exportReport: (params) => api.get('/invoices/report/export', { params, responseType: 'blob' }),
};

// Admin APIs
export const adminAPI = {
  createAdmin: (data) => api.post('/admin/users', data).then(res => res.data.data),
  getAdmins: () => api.get('/admin/users').then(res => res.data.data),
  updateAdmin: (id, data) => api.put(`/admin/users/${id}`, data).then(res => res.data.data),
  updateAdminStatus: (id, attivo) => api.patch(`/admin/users/${id}/status`, { attivo }).then(res => res.data.data),
  resetAdminPassword: (id) => api.post(`/admin/users/${id}/reset-password`).then(res => res.data),
  deleteAdmin: (id) => api.delete(`/admin/users/${id}`).then(res => res.data),
  resetDatabase: () => api.post('/admin/reset').then(res => res.data),
};

export default api;
